import { useState } from 'react';
import { useAuditLog, useMerkleRoot } from '../hooks/useAudit';
import { verifyMerkleProof } from '@aigftpwatch/shared';
import type { AuditLogEntry } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { Modal } from '../components/ui/Modal';
import { ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{
    entity_type?: string;
    action?: string;
  }>({});
  const { data, isLoading } = useAuditLog({ ...filters, page, per_page: 50 });
  const { data: merkleRoot } = useMerkleRoot();
  const [verifyId, setVerifyId] = useState<number | null>(null);
  const [verifyResult, setVerifyResult] = useState<boolean | null>(null);
  const [verifying, setVerifying] = useState(false);

  async function handleVerify(auditId: number, entryHash: string) {
    setVerifyId(auditId);
    setVerifyResult(null);
    setVerifying(true);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const proofResponse = await fetch(`/api/merkle/proof/${auditId}`);
      const proofData = await proofResponse.json();
      if (!proofData.success) {
        toast.error('Could not fetch proof');
        return;
      }

      const rootResponse = await fetch('/api/merkle/root');
      const rootData = await rootResponse.json();

      const valid = await verifyMerkleProof(
        entryHash,
        proofData.data.proof,
        rootData.data.hash,
      );
      setVerifyResult(valid);
      if (valid) {
        toast.success('Entry verified - Merkle proof valid');
      } else {
        toast.error('Verification FAILED - proof is invalid');
      }
    } catch {
      toast.error('Verification error');
      setVerifyResult(false);
    } finally {
      setVerifying(false);
    }
  }

  const entries = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Merkle Root Info */}
      <Card className="border-gold-200 bg-gold-50">
        <div className="flex items-center gap-4">
          <ShieldCheckIcon className="h-8 w-8 text-gold-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gold-800">Merkle Root</p>
            <p className="mt-1 break-all font-mono text-xs text-gold-700">
              {merkleRoot?.hash ?? 'Loading...'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-gold-800">{merkleRoot?.count ?? 0}</p>
            <p className="text-xs text-gold-600">audit entries</p>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={filters.entity_type ?? ''}
          onChange={(e) => { setFilters({ ...filters, entity_type: e.target.value || undefined }); setPage(1); }}
          className="rounded-lg border border-silver-300 px-3 py-1.5 text-sm"
        >
          <option value="">All Entity Types</option>
          {['account', 'product', 'inventory', 'outshipment', 'transfer', 'new_account_request', 'address', 'sftp_job'].map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={filters.action ?? ''}
          onChange={(e) => { setFilters({ ...filters, action: e.target.value || undefined }); setPage(1); }}
          className="rounded-lg border border-silver-300 px-3 py-1.5 text-sm"
        >
          <option value="">All Actions</option>
          {['create', 'update', 'delete', 'sftp_upload', 'sftp_result', 'reconcile'].map((a) => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <p className="text-sm text-silver-500">{pagination?.total ?? 0} entries</p>
      </div>

      {/* Audit Table */}
      <Card padding={false}>
        <Table<AuditLogEntry>
          loading={isLoading}
          data={entries}
          columns={[
            { key: 'id', header: 'ID' },
            { key: 'timestamp', header: 'Time', render: (r) => r.timestamp ? new Date(r.timestamp).toLocaleString() : '-' },
            { key: 'entity_type', header: 'Entity', render: (r) => r.entity_type.replace(/_/g, ' ') },
            { key: 'entity_id', header: 'Entity ID' },
            { key: 'action', header: 'Action', render: (r) => <Badge>{r.action}</Badge> },
            { key: 'user_email', header: 'User' },
            {
              key: 'entry_hash', header: 'Hash',
              render: (r) => (
                <span className="font-mono text-xs text-silver-500">
                  {(r.entry_hash ?? '').substring(0, 12)}...
                </span>
              ),
            },
            {
              key: 'verify', header: '',
              render: (r) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleVerify(r.id, r.entry_hash)}
                  loading={verifying && verifyId === r.id}
                >
                  <ShieldCheckIcon className="h-4 w-4" /> Verify
                </Button>
              ),
            },
          ]}
        />
        {pagination && (
          <div className="px-4">
            <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      {/* Verify Result Modal */}
      <Modal open={verifyResult !== null} onClose={() => setVerifyResult(null)} title="Merkle Proof Verification">
        <div className="flex flex-col items-center gap-4 py-4">
          {verifyResult ? (
            <>
              <ShieldCheckIcon className="h-16 w-16 text-emerald-500" />
              <p className="text-lg font-bold text-emerald-700">Proof Valid</p>
              <p className="text-sm text-silver-500">This audit entry is cryptographically verified in the Merkle tree.</p>
            </>
          ) : (
            <>
              <ShieldExclamationIcon className="h-16 w-16 text-red-500" />
              <p className="text-lg font-bold text-red-700">Verification Failed</p>
              <p className="text-sm text-silver-500">This audit entry could not be verified. The data may have been tampered with.</p>
            </>
          )}
          <Button onClick={() => setVerifyResult(null)}>Close</Button>
        </div>
      </Modal>
    </div>
  );
}
